import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { FadeIn } from './ui/FadeIn';
import { Headset, Smartphone, Tablet, Laptop, Tv, Move, Maximize2 } from 'lucide-react';
export function Solution() {
  const devices = [{
    icon: <Headset size={24} data-id="element-206" />,
    label: 'VR Headsets'
  }, {
    icon: <Smartphone size={24} data-id="element-207" />,
    label: 'Phones'
  }, {
    icon: <Tablet size={24} data-id="element-208" />,
    label: 'Tablets'
  }, {
    icon: <Laptop size={24} data-id="element-209" />,
    label: 'Laptops'
  }, {
    icon: <Tv size={24} data-id="element-210" />,
    label: 'Smart TVs'
  }];
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  // Calculate drag bounds dynamically (inner is wider than container)
  const getBounds = () => {
    if (!containerRef.current || !innerRef.current) return {
      left: 0,
      right: 0
    };
    const containerWidth = containerRef.current.offsetWidth;
    const innerWidth = innerRef.current.offsetWidth;
    return {
      left: -(innerWidth - containerWidth),
      right: 0
    };
  };
  const handleDragStart = () => {
    if (!hasInteracted) setHasInteracted(true);
  };
  // Gentle idle nudge animation until user interacts
  useEffect(() => {
    if (hasInteracted) return;
    const timer = setTimeout(() => {
      const bounds = getBounds();
      // Subtle hint pan
      animate(x, [0, bounds.left * 0.15, 0], {
        duration: 6,
        ease: 'easeInOut',
        repeat: Infinity
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [hasInteracted, x]);
  return <section className="py-32 bg-zinc-950 relative overflow-hidden" data-id="element-211">
      {/* Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-900/20 rounded-full blur-[120px] pointer-events-none" data-id="element-212" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-213">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-214">
          <FadeIn data-id="element-215">
            <span className="text-sm font-medium tracking-widest uppercase text-zinc-500 mb-6 block" data-id="element-216">
              Our Solution
            </span>
            <h2 className="text-4xl md:text-6xl font-display font-medium text-white mb-6" data-id="element-217">
              Step inside, from anywhere.
            </h2>
            <p className="text-xl text-zinc-400 font-light" data-id="element-218">
              Experience immersive 360 virtual tours that make you feel like
              you're actually there. Accessible instantly across all your
              devices.
            </p>
          </FadeIn>
        </div>

        {/* Device Compatibility Row */}
        <FadeIn delay={0.2} className="flex flex-wrap justify-center gap-8 md:gap-16 mb-20" data-id="element-219">
          {devices.map((device, idx) => <div key={idx} className="flex flex-col items-center gap-3 text-zinc-500 hover:text-white transition-colors" data-id="element-220">
              <div className="p-4 rounded-2xl glass-panel" data-id="element-221">{device.icon}</div>
              <span className="text-sm font-medium" data-id="element-222">{device.label}</span>
            </div>)}
        </FadeIn>

        {/* Interactive 360° Virtual Tour Viewer */}
        <FadeIn delay={0.4} className="relative mx-auto max-w-6xl" data-id="element-223">
          <div ref={containerRef} className="aspect-[21/9] md:aspect-[2.4/1] rounded-3xl overflow-hidden glass-panel p-2" data-id="element-224">
            <div className="w-full h-full rounded-2xl overflow-hidden relative bg-zinc-900 cursor-grab active:cursor-grabbing select-none" data-id="element-225">
              {/* Draggable panorama */}
              <motion.div ref={innerRef} className="absolute inset-y-0 left-0 h-full flex" style={{
              x,
              width: '260%',
              touchAction: 'pan-y'
            }} drag="x" dragConstraints={getBounds()} dragElastic={0.08} dragMomentum={true} onDragStart={handleDragStart} data-id="element-226">
                <img src="https://cdn.magicpatterns.com/uploads/6B9Vyce6SW7ZRgV3NuokZG/diamond_city_vr_(22).jpg" alt="360 degree virtual tour panorama — drag to explore" className="w-full h-full object-cover pointer-events-none" draggable={false} data-id="element-227" />
              </motion.div>

              {/* Subtle vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-zinc-950/20 pointer-events-none" data-id="element-228" />

              {/* Top-left scene chip */}
              <div className="absolute top-4 left-4 md:top-6 md:left-6 glass-panel px-4 py-2 rounded-full flex items-center gap-2 pointer-events-none" data-id="element-229">
                <span className="relative flex h-2 w-2" data-id="element-230">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-231"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" data-id="element-232"></span>
                </span>
                <span className="text-xs font-medium text-white tracking-wide" data-id="element-233">
                  LIVE 360° TOUR
                </span>
              </div>

              {/* Top-right fullscreen */}
              <div className="absolute top-4 right-4 md:top-6 md:right-6 glass-panel p-2.5 rounded-full text-white/80 pointer-events-none" data-id="element-234">
                <Maximize2 size={16} data-id="element-235" />
              </div>

              {/* Drag hint - fades after interaction */}
              {!hasInteracted && <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" initial={{
              opacity: 0
            }} animate={{
              opacity: [0, 1, 1, 0.6]
            }} transition={{
              duration: 2,
              delay: 0.5
            }} data-id="element-236">
                  <motion.div className="glass-panel px-5 py-3 rounded-full flex items-center gap-3 text-white" animate={{
                x: [0, 12, -12, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-237">
                    <Move size={18} data-id="element-238" />
                    <span className="text-sm font-medium" data-id="element-239">Drag to explore</span>
                  </motion.div>
                </motion.div>}

              {/* Bottom control bar */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass-panel px-5 py-3 rounded-full flex items-center gap-4 md:gap-6 whitespace-nowrap pointer-events-none" data-id="element-240">
                <div className="flex items-center gap-2" data-id="element-241">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" data-id="element-242" />
                  <span className="text-sm font-medium text-white" data-id="element-243">
                    Living Room
                  </span>
                </div>
                <div className="w-px h-4 bg-white/20" data-id="element-244" />
                <div className="flex items-center gap-2 text-zinc-400" data-id="element-245">
                  <Move size={14} data-id="element-246" />
                  <span className="text-xs md:text-sm" data-id="element-247">360° Panorama</span>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}